/*
 * Carbisdale - SearchManager
 *
 * Inkrementelle Suche im Editor.
 * Cmd+F/Ctrl+F oeffnet ein Such-Popup oben rechts,
 * Tippen sucht inkrementell, Enter/Shift+Enter navigiert.
 * Nutzt SingleLineInput fuer Text-Eingabe mit Cursor, Selection und Scroll.
 */

import { Style } from './config.js'
import { SingleLineInput } from './SingleLineInput.js'
import { blurKeyboardSink } from './touchKeyboard.js'

/**
 * SearchManager-Klasse
 */
export class SearchManager {
    /**
     * @param {Overlay} overlay - Der Overlay fuer die Darstellung
     * @param {EditorModel} model - Das Editor-Datenmodell
     */
    constructor(overlay, model) {
        this.overlay = overlay
        this.model = model

        // Such-State
        this.isOpen = false

        // Text-Eingabe (Cursor, Selection, Scroll)
        this._input = new SingleLineInput({
            onChange: () => this._performSearch(),
            onEnter: () => {},        // Enter wird von SearchManager behandelt
            onNavigateOut: () => {}   // Nicht benoetigt
        })

        // Treffer
        this.matches = []
        this.currentMatchIndex = -1

        // Cursor-Blinken im Suchfeld
        this.cursorVisible = true
        this.cursorBlinkInterval = null

        // Popup-Dimensionen
        this.popupWidth = 30
        this.popupX = 0
        this.popupY = 1

        // Map: paragraphId → Array von Matches (für O(1) Lookup pro Paragraph)
        this._matchesByParagraph = new Map()
    }

    // Backward-kompatible Accessor fuer searchTerm/searchCursorPos
    get searchTerm() { return this._input.text }
    set searchTerm(value) { this._input.text = value || '' }

    get searchCursorPos() { return this._input.cursorPos }
    set searchCursorPos(value) { this._input.cursorPos = value }

    // ===== Public API =====

    /**
     * Oeffnet die Suche.
     * @param {number} cols - Aktuelle Screen-Breite fuer Positionierung
     * @param {{paragraphId: number, offset: number}} cursorPos - Aktuelle Cursor-Position
     */
    open(cols, cursorPos) {
        // Phase 6.0d: Soft-Keyboard schliessen — Suche hat eigene SingleLineInput,
        // die ueber den keydown-Pfad gefuettert wird; eine zweite Soft-Keyboard-
        // Eingabe parallel waere doppeldeutig.
        blurKeyboardSink()
        this.isOpen = true
        this._input.setText('')
        this._input.setVisibleWidth(this.popupWidth - 4)
        this.matches = []
        this.currentMatchIndex = -1
        this._matchesByParagraph = new Map()

        // Position: oben rechts
        this.popupX = Math.max(0, cols - this.popupWidth - 2)
        this.popupY = 1

        this._startCursorBlink()
        this._renderPopup()

        // Startposition fuer Suche merken
        this._searchStartPos = cursorPos || null
    }

    /**
     * Schliesst die Suche. Cursor bleibt am aktuellen Match.
     * @returns {{paragraphId: number, offset: number}|null} Position des aktuellen Matches
     */
    close() {
        this._stopCursorBlink()
        this.isOpen = false
        this.overlay.deactivate()

        const result = this.currentMatchIndex >= 0 && this.currentMatchIndex < this.matches.length
            ? { paragraphId: this.matches[this.currentMatchIndex].paragraphId,
                offset: this.matches[this.currentMatchIndex].offset,
                field: this.matches[this.currentMatchIndex].field || 'text' }
            : null

        this.matches = []
        this.currentMatchIndex = -1
        this._matchesByParagraph = new Map()

        return result
    }

    /**
     * Prueft ob die Suche offen ist.
     * @returns {boolean}
     */
    isSearchOpen() {
        return this.isOpen
    }

    /**
     * Verarbeitet einen Tastendruck.
     * @param {KeyboardEvent} e
     * @returns {boolean} true wenn Event behandelt wurde
     */
    handleKeydown(e) {
        if (!this.isOpen) return false

        const key = e.key

        // Escape schliesst die Suche
        if (key === 'Escape') {
            this.close()
            return true
        }

        // Enter / Shift+Enter: naechster/vorheriger Treffer
        if (key === 'Enter') {
            if (e.shiftKey) {
                this._navigatePrevious()
            } else {
                this._navigateNext()
            }
            this._renderPopup()
            return true
        }

        // Cmd+V / Ctrl+V: Einfügen aus Zwischenablage
        if ((e.metaKey || e.ctrlKey) && !e.altKey && (key === 'v' || key === 'V')) {
            navigator.clipboard.readText().then(text => {
                if (text) this.handlePaste(text)
            })
            return true
        }

        // Modifier-Only ignorieren
        if (['Shift', 'Control', 'Alt', 'Meta', 'CapsLock'].includes(key)) {
            return false
        }

        // Alle anderen Tasten an SingleLineInput delegieren
        this._input.handleKeydown(e)
        this._renderPopup()
        return true
    }

    /**
     * Verarbeitet Paste-Events.
     * @param {string} text - Eingefuegter Text
     */
    handlePaste(text) {
        if (!this.isOpen) return
        this._input.handlePaste(text)
        this._renderPopup()
    }

    /**
     * Gibt den Highlight-Typ fuer eine Position zurueck.
     * @param {number} paragraphId
     * @param {number} offset
     * @param {string} field - 'text' oder 'note' (default 'text')
     * @returns {null|'match'|'current'} null wenn kein Match
     */
    getHighlightAt(paragraphId, offset, field = 'text') {
        if (!this.isOpen || this.matches.length === 0) return null

        // O(1)-Lookup: nur Matches dieses Paragraphs prüfen
        const paraMatches = this._matchesByParagraph.get(paragraphId)
        if (!paraMatches) return null

        for (const { match, globalIndex } of paraMatches) {
            if (match.field === field &&
                offset >= match.offset && offset < match.offset + match.length) {
                return globalIndex === this.currentMatchIndex ? 'current' : 'match'
            }
        }

        return null
    }

    // ===== Private: Suche =====

    /**
     * Fuehrt die Suche durch (case-insensitive, alle Paragraphen).
     * Links ([[@id]]) werden als aufgeloeste Labels durchsucht.
     */
    _performSearch() {
        this.matches = []
        this._matchesByParagraph = new Map()

        if (this.searchTerm.length === 0) {
            this.currentMatchIndex = -1
            return
        }

        const term = this.searchTerm.toLowerCase()
        const separatorMap = this.model.buildSeparatorMap()

        for (const para of this.model.paragraphs) {
            // Text durchsuchen
            this._findMatchesInField(para, 'text', para.text, term, separatorMap)

            // Note durchsuchen
            if (para.note && para.note.length > 0) {
                this._findMatchesInField(para, 'note', para.note, term, separatorMap)
            }

            // Info durchsuchen
            if (para.info && para.info.length > 0) {
                this._findMatchesInField(para, 'info', para.info, term, separatorMap)
            }
        }

        // Naechsten Match ab Cursor-Position waehlen
        this._selectClosestMatch()
    }

    /**
     * Sucht Matches in einem Feld eines Paragraphs.
     * Wenn Links vorhanden, wird im aufgeloesten Text gesucht
     * und die Treffer-Offsets zurueck auf Data-Offsets konvertiert.
     * @param {Object} para - Der Paragraph
     * @param {string} field - 'text', 'note' oder 'info'
     * @param {string} fieldText - Der zu durchsuchende Text
     * @param {string} term - Der Suchbegriff (lowercase)
     * @param {Map} separatorMap - Separator-Map fuer Link-Aufloesung
     */
    _findMatchesInField(para, field, fieldText, term, separatorMap) {
        // Links im Feld aufloesen fuer die Suche
        let searchText, searchToData

        // RT-Modus: Links in para.links[field], \uFFFC als Platzhalter
        const links = para.links && para.links[field]
        if (links && links.length > 0) {
            const resolved = this._resolveRichTextLinksForSearch(fieldText, links, separatorMap)
            searchText = resolved.searchText
            searchToData = resolved.searchToData
        } else {
            searchText = fieldText
            searchToData = null
        }

        const text = searchText.toLowerCase()
        let pos = 0
        while (pos < text.length) {
            const idx = text.indexOf(term, pos)
            if (idx === -1) break

            let offset, length
            if (searchToData) {
                // Link-Mapping: Search-Position → Data-Position
                const dataStart = searchToData[idx]
                const dataEnd = searchToData[idx + term.length - 1]
                offset = dataStart
                length = dataEnd - dataStart + 1
            } else {
                offset = idx
                length = term.length
            }

            const match = {
                paragraphId: para.id,
                offset,
                length,
                field
            }
            const globalIndex = this.matches.length
            this.matches.push(match)

            // In Paragraph-Map gruppieren
            let paraMatches = this._matchesByParagraph.get(para.id)
            if (!paraMatches) {
                paraMatches = []
                this._matchesByParagraph.set(para.id, paraMatches)
            }
            paraMatches.push({ match, globalIndex })

            pos = idx + 1
        }
    }

    /**
     * RT-Modus: Ersetzt \uFFFC-Platzhalter durch aufgeloeste Link-Labels.
     * @param {string} fieldText - Text mit \uFFFC-Platzhaltern
     * @param {Array<{offset: number, targetId: number}>} links - Link-Eintraege
     * @param {Map} separatorMap - Separator-Map
     * @returns {{searchText: string, searchToData: number[]}}
     */
    _resolveRichTextLinksForSearch(fieldText, links, separatorMap) {
        // Link-Map aufbauen: offset → {targetId, label}
        const linkMap = new Map()
        for (const link of links) {
            const { label } = this.model.resolveLinkLabel(link.targetId, separatorMap)
            linkMap.set(link.offset, label)
        }

        const searchChars = []
        const searchToData = []

        for (let i = 0; i < fieldText.length; i++) {
            if (fieldText[i] === '\uFFFC' && linkMap.has(i)) {
                const label = linkMap.get(i)
                for (let c = 0; c < label.length; c++) {
                    searchChars.push(label[c])
                    searchToData.push(i) // Alle Label-Chars → \uFFFC data-offset
                }
            } else {
                searchChars.push(fieldText[i])
                searchToData.push(i)
            }
        }

        return { searchText: searchChars.join(''), searchToData }
    }

    /**
     * Waehlt den naechsten Match ab der Editor-Cursor-Position.
     */
    _selectClosestMatch() {
        if (this.matches.length === 0) {
            this.currentMatchIndex = -1
            return
        }

        // Wenn keine Startposition, ersten Match nehmen
        if (!this._searchStartPos) {
            this.currentMatchIndex = 0
            return
        }

        const startParaIndex = this.model.getParagraphIndex(this._searchStartPos.paragraphId)
        const startOffset = this._searchStartPos.offset

        // Suche den ersten Match ab der aktuellen Position
        for (let i = 0; i < this.matches.length; i++) {
            const m = this.matches[i]
            const mParaIndex = this.model.getParagraphIndex(m.paragraphId)

            if (mParaIndex > startParaIndex ||
                (mParaIndex === startParaIndex && m.offset >= startOffset)) {
                this.currentMatchIndex = i
                return
            }
        }

        // Kein Match nach Cursor: Wrap-Around, nehme ersten
        this.currentMatchIndex = 0
    }

    /**
     * Navigiert zum naechsten Treffer.
     */
    _navigateNext() {
        if (this.matches.length === 0) return
        this.currentMatchIndex = (this.currentMatchIndex + 1) % this.matches.length
    }

    /**
     * Navigiert zum vorherigen Treffer.
     */
    _navigatePrevious() {
        if (this.matches.length === 0) return
        this.currentMatchIndex = (this.currentMatchIndex - 1 + this.matches.length) % this.matches.length
    }

    /**
     * Gibt die aktuelle Match-Position zurueck (fuer Cursor-Synchronisation).
     * @returns {{paragraphId: number, offset: number}|null}
     */
    getCurrentMatchPosition() {
        if (this.currentMatchIndex < 0 || this.currentMatchIndex >= this.matches.length) {
            return null
        }
        const m = this.matches[this.currentMatchIndex]
        return { paragraphId: m.paragraphId, offset: m.offset, field: m.field || 'text' }
    }

    // ===== Private: Rendering =====

    /**
     * Rendert das Such-Popup im Overlay.
     */
    _renderPopup() {
        this.overlay.clear()

        const x = this.popupX
        const y = this.popupY
        const w = this.popupWidth
        const h = 3  // Rahmen oben + Suchfeld + Rahmen unten

        // Box ohne Titel (Titel als proportionaler textSpan)
        this.overlay.drawMenuFrame(
            { x, y, width: w, height: h },
            { borderStyle: Style.MENU_BORDER, bgStyle: Style.MENU_BACKGROUND }
        )

        // Titel "Find" als textSpan (proportional im Border). clearBackground
        // sorgt dafuer, dass der Border-Path unter dem Title blickdicht
        // ueberdeckt wird — sonst laeuft die Linie sichtbar durch.
        this.overlay.addTextSpan(x + 2, y, ' Find ', Style.MENU_TITLE, w - 4, { clearBackground: true })

        // Suchfeld rendern
        this._renderSearchField(x, y)

        this.overlay.isActive = true
        this.overlay.markDirty()
    }

    /**
     * Rendert das Suchfeld innerhalb des Popups als textSpans.
     * Wird auch vom Cursor-Blink-Handler aufgerufen.
     */
    _renderSearchField(boxX, boxY) {
        // Bestehende Field-Spans entfernen (Titel-Span bleibt bei _renderPopup,
        // bei Blink-Update werden alle Spans neu aufgebaut)
        this.overlay.textSpans = []

        const fieldY = boxY + 1
        const fieldX = boxX + 1
        const fieldWidth = this.popupWidth - 2  // Abzueglich Rahmen links/rechts

        // Titel-Span (wird bei jedem Update mit-erneuert) — siehe _renderPopup
        // fuer die clearBackground-Begruendung.
        this.overlay.addTextSpan(boxX + 2, boxY, ' Find ', Style.MENU_TITLE, this.popupWidth - 4, { clearBackground: true })

        // Counter berechnen (z.B. "3/17")
        let counter = ''
        if (this.searchTerm.length > 0) {
            if (this.matches.length === 0) {
                counter = '0/0'
            } else {
                counter = `${this.currentMatchIndex + 1}/${this.matches.length}`
            }
        }

        // Verfuegbare Breite fuer Suchtext
        const counterWidth = counter.length > 0 ? counter.length + 1 : 0
        const textFieldWidth = fieldWidth - counterWidth

        // SingleLineInput-State abrufen
        const state = this._input.getState()

        // Sichtbaren Text berechnen (mit scrollOffset)
        const displayText = state.text.substring(state.scrollOffset)
        const displayCursor = state.cursorPos - state.scrollOffset

        // Selection-Range auf Display-Koordinaten anpassen
        let selStart = null
        let selEnd = null
        if (state.selectionStart != null && state.selectionStart !== state.selectionEnd) {
            selStart = Math.max(0, Math.min(state.selectionStart, state.selectionEnd) - state.scrollOffset)
            selEnd = Math.max(0, Math.max(state.selectionStart, state.selectionEnd) - state.scrollOffset)
        }

        // Suchtext als textSpan
        this.overlay.addTextSpan(fieldX, fieldY, displayText, Style.DIALOG_TEXT, textFieldWidth, {
            cursorPos: this.cursorVisible ? displayCursor : -1,
            cursorVisible: this.cursorVisible,
            selectionStart: selStart,
            selectionEnd: selEnd
        })

        // Counter als textSpan (rechtsbuendig)
        if (counter.length > 0) {
            const counterStyle = this.matches.length === 0 ? Style.EDITOR_MARKER : Style.PANE_INFO
            this.overlay.addTextSpan(fieldX, fieldY, counter, counterStyle, fieldWidth, {
                align: 'right'
            })
        }
    }

    // ===== Private: Cursor-Blinken =====

    _startCursorBlink() {
        this._stopCursorBlink()
        this.cursorVisible = true
        this.cursorBlinkInterval = setInterval(() => {
            this.cursorVisible = !this.cursorVisible
            if (this.isOpen) {
                this._renderSearchField(this.popupX, this.popupY)
                this.overlay.markDirty()
            }
        }, 500)
    }

    _stopCursorBlink() {
        if (this.cursorBlinkInterval) {
            clearInterval(this.cursorBlinkInterval)
            this.cursorBlinkInterval = null
        }
    }
}
